import { getSession } from 'next-auth/react';
import { env } from 'next-runtime-env';

import { RequestOptions, TUploadProgressEvent } from '../types';

import fetchInstance, { FetchConfig } from './fetchClient';
import { getServerSession } from 'next-auth';

const buildConfig = async (options?: Partial<RequestOptions>): Promise<FetchConfig> => {
  options = options ?? {};
  const config: FetchConfig = {};

  if (options.baseUrl && options.useMock) {
    throw new Error(
      'Cannot use both baseUrl and useMock options simultaneously. Please specify only one.'
    );
  }

  if (options.headers) config.headers = options.headers;
  if (options.timeout) config.timeout = options.timeout;
  if (options.baseUrl) config.baseURL = options.baseUrl;
  if (!options.baseUrl && typeof window === 'undefined')
    config.baseURL = env('API_BACK_TO_BACK_URL');
  if (options.useMock) config.baseURL = env('NEXT_PUBLIC_MOCK_BASE_URL');
  if (options.responseType) config.responseType = options.responseType;
  if (options.queryParams) config.params = options.queryParams;

  if (options.onUploadProgress) {
    config.onUploadProgress = (progressEvent) => {
      options!.onUploadProgress!(progressEvent as TUploadProgressEvent);
    };
  }
  if (!(options?.anonymouse ?? false)) {
    let session;
    if (typeof window !== 'undefined') {
      session = await getSession();
    } else {
      session = await getServerSession();
    }

    if (session?.accessToken) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${session.accessToken}`,
        'oauth-access-token': `${session.accessToken}`,
      };
    }
  }

  return config;
};

class ApiClient {
  async get<T>(url: string, options?: Partial<RequestOptions>): Promise<T> {
    const config = await buildConfig(options);
    const response = await fetchInstance.get<T>(url, config);
    return response.data;
  }

  async post<TRequest, TResponse>(
    url: string,
    data?: TRequest,
    options?: Partial<RequestOptions>
  ): Promise<TResponse> {
    const config = await buildConfig(options);
    const response = await fetchInstance.post<TResponse>(url, data, config);
    return response.data;
  }

  async put<TRequest, TResponse>(
    url: string,
    data?: TRequest,
    options?: Partial<RequestOptions>
  ): Promise<TResponse> {
    const config = await buildConfig(options);
    const response = await fetchInstance.put<TResponse>(url, data, config);
    return response.data;
  }

  async delete<TRequest, TResponse>(
    url: string,
    data?: TRequest,
    options?: Partial<RequestOptions>
  ): Promise<TResponse> {
    const config = await buildConfig(options);
    const response = await fetchInstance.delete<TResponse>(url, data, config);
    return response.data;
  }

  async patch<TRequest, TResponse>(
    url: string,
    data?: TRequest,
    options?: Partial<RequestOptions>
  ): Promise<TResponse> {
    const config = await buildConfig(options);
    const response = await fetchInstance.patch<TResponse>(url, data, config);
    return response.data;
  }
}

export const ApiClientInstance = new ApiClient();
